import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ScrollLockService {
  private lockCount = 0;
  private scrollY = 0;
  private previousStyles: Partial<CSSStyleDeclaration> | null = null;
  private scrollContainer: HTMLElement | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  lock(): void {
    if (this.lockCount === 0) {
      if (this.isMobile()) {
        this.lockMobileScrollContainer();
      } else {
        this.lockBodyScroll();
      }
    }

    this.lockCount += 1;
  }

  unlock(): void {
    if (this.lockCount === 0) {
      return;
    }

    this.lockCount -= 1;
    if (this.lockCount === 0) {
      if (this.scrollContainer) {
        this.unlockMobileScrollContainer();
      } else {
        this.unlockBodyScroll();
      }
    }
  }

  /** iPhone/H5 實際滾動容器是 .content，不是 body */
  private isMobile(): boolean {
    return this.document.body.classList.contains('is-mobile');
  }

  private getMobileScrollContainer(): HTMLElement | null {
    return this.document.querySelector('.basic-layout .content') as HTMLElement | null;
  }

  private lockBodyScroll(): void {
    const body = this.document.body;
    const docEl = this.document.documentElement;

    this.scrollY = window.scrollY || docEl.scrollTop || body.scrollTop || 0;
    this.previousStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
    };

    body.style.position = 'fixed';
    body.style.top = `-${this.scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'contain';
  }

  private unlockBodyScroll(): void {
    const body = this.document.body;
    const prev = this.previousStyles;

    if (prev) {
      body.style.position = prev.position || '';
      body.style.top = prev.top || '';
      body.style.width = prev.width || '';
      body.style.overflow = prev.overflow || '';
      body.style.overscrollBehavior = prev.overscrollBehavior || '';
    } else {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      body.style.overscrollBehavior = '';
    }

    window.scrollTo(0, this.scrollY);
    this.previousStyles = null;
  }

  private lockMobileScrollContainer(): void {
    const content = this.getMobileScrollContainer();
    if (!content) {
      this.lockBodyScroll();
      return;
    }

    this.scrollContainer = content;
    this.scrollY = content.scrollTop;
    const rect = content.getBoundingClientRect();
    this.previousStyles = {
      position: content.style.position,
      top: content.style.top,
      left: content.style.left,
      width: content.style.width,
      height: content.style.height,
      overflow: content.style.overflow,
      touchAction: content.style.touchAction,
    };

    content.style.position = 'fixed';
    content.style.top = `${rect.top}px`;
    content.style.left = `${rect.left}px`;
    content.style.width = `${rect.width}px`;
    content.style.height = `${rect.height}px`;
    content.style.overflow = 'hidden';
    content.style.touchAction = 'none';
  }

  private unlockMobileScrollContainer(): void {
    const content = this.scrollContainer;
    if (!content) return;

    const prev = this.previousStyles;
    if (prev) {
      content.style.position = prev.position || '';
      content.style.top = prev.top || '';
      content.style.left = prev.left || '';
      content.style.width = prev.width || '';
      content.style.height = prev.height || '';
      content.style.overflow = prev.overflow || '';
      content.style.touchAction = prev.touchAction || '';
    } else {
      content.style.position = '';
      content.style.top = '';
      content.style.left = '';
      content.style.width = '';
      content.style.height = '';
      content.style.overflow = '';
      content.style.touchAction = '';
    }

    content.scrollTop = this.scrollY;
    this.scrollContainer = null;
    this.previousStyles = null;
  }
}
